import React from 'react';
import classnames from 'classnames';
import { makeStyles } from '@material-ui/core';
import { usePaymentOptionsAnimation } from 'payment/hooks';

const styles = makeStyles(({ palette }) => ({
  segmentFill: {
    fill: palette.text.primary,
  },
  lineFill: {
    stroke: palette.common.white,
  },
  segmentLeftTop: {
    animation: '$leftTop ease-in-out 1s',
    animationFillMode: 'forwards',
  },
  segmentRightTop: {
    animation: '$rightTop ease-in-out 1s',
    animationFillMode: 'forwards',
  },
  segmentLeftBottom: {
    animation: '$leftBottom ease-in-out 1s',
    animationFillMode: 'forwards',
  },
  segmentRightBottom: {
    animation: '$rightBottom ease-in-out 1s',
    animationFillMode: 'forwards',
  },
  '@keyframes leftTop': {
    '0%': {
      transform: 'translate(0px, 0px)',
    },
    '90%': {
      transform: 'translate(-4px, -4px)',
    },
    '100%': {
      transform: 'translate(-3px, -3px)',
    },
  },
  '@keyframes rightTop': {
    '0%': {
      transform: 'translate(0px, 0px)',
    },
    '90%': {
      transform: 'translate(4px, -4px)',
    },
    '100%': {
      transform: 'translate(3px, -3px)',
    },
  },
  '@keyframes leftBottom': {
    '0%': {
      transform: 'translate(0px, 0px)',
    },
    '90%': {
      transform: 'translate(-4px, 4px)',
    },
    '100%': {
      transform: 'translate(-3px, 3px)',
    },
  },
  '@keyframes rightBottom': {
    '0%': {
      transform: 'translate(0px, 0px)',
    },
    '60%': {
      fill: palette.success.main,
    },
    '90%': {
      transform: 'translate(4px, 4px)',
      fill: palette.success.main,
    },
    '100%': {
      transform: 'translate(3px, 3px)',
      fill: palette.success.main,
    },
  },
  segmentLeftTopDeselected: {
    animation: '$leftTopDeselected ease-in-out 1s',
    animationFillMode: 'forwards',
  },
  segmentRightTopDeselected: {
    animation: '$rightTopDeselected ease-in-out 1s',
    animationFillMode: 'forwards',
  },
  segmentLeftBottomDeselected: {
    animation: '$leftBottomDeselected ease-in-out 1s',
    animationFillMode: 'forwards',
  },
  segmentRightBottomDeselected: {
    animation: '$rightBottomDeselected ease-in-out 1s',
    animationFillMode: 'forwards',
  },
  '@keyframes leftTopDeselected': {
    '0%': {
      transform: 'translate(-3px, -3px)',
    },
    '100%': {
      transform: 'translate(0px, 0px)',
    },
  },
  '@keyframes rightTopDeselected': {
    '0%': {
      transform: 'translate(3px, -3px)',
    },
    '100%': {
      transform: 'translate(0px, 0px)',
    },
  },
  '@keyframes leftBottomDeselected': {
    '0%': {
      transform: 'translate(-3px, 3px)',
    },
    '100%': {
      transform: 'translate(0px, 0px)',
    },
  },
  '@keyframes rightBottomDeselected': {
    '0%': {
      transform: 'translate(3px, 3px)',
      fill: palette.success.main,
    },
    '100%': {
      transform: 'translate(0px, 0px)',
    },
  },
  horizontalLine: {
    animation: '$horizontalLine ease-in-out 500ms',
  },
  verticalLine: {
    animation: '$verticalLine ease-in-out 500ms',
  },
  '@keyframes horizontalLine': {
    '0%': {
      transform: 'translateX(-80px)',
    },
    '100%': {
      transform: 'translateX(0px)',
    },
  },
  '@keyframes verticalLine': {
    '0%': {
      transform: 'translateY(-80px)',
    },
    '100%': {
      transform: 'translateY(0px)',
    },
  },
}));

interface SplitBillEvenlyProps {
  isSelected: boolean;
}

const SplitBillEvenly = ({ isSelected }: SplitBillEvenlyProps) => {
  const classes = styles();
  const { playSelectedAnimation, playDeselectedAnimation } = usePaymentOptionsAnimation(isSelected);

  const leftTopAnimation = classnames([classes.segmentFill], {
    [classes.segmentLeftTop]: playSelectedAnimation,
    [classes.segmentLeftTopDeselected]: playDeselectedAnimation,
  });
  const rightTopAnimation = classnames([classes.segmentFill], {
    [classes.segmentRightTop]: playSelectedAnimation,
    [classes.segmentRightTopDeselected]: playDeselectedAnimation,
  });
  const leftBottomAnimation = classnames([classes.segmentFill], {
    [classes.segmentLeftBottom]: playSelectedAnimation,
    [classes.segmentLeftBottomDeselected]: playDeselectedAnimation,
  });
  const rightBottomAnimation = classnames([classes.segmentFill], {
    [classes.segmentRightBottom]: playSelectedAnimation,
    [classes.segmentRightBottomDeselected]: playDeselectedAnimation,
  });

  const horizontalAnimation = classnames([classes.lineFill], {
    [classes.horizontalLine]: playSelectedAnimation,
  });

  const verticalAnimation = classnames([classes.lineFill], {
    [classes.verticalLine]: playSelectedAnimation,
  });

  return (
    <svg
      width="106"
      height="106"
      viewBox="0 0 106 106"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-label="split bill evenly icon"
    >
      <g>
        <path
          id="rightTop"
          className={rightTopAnimation}
          d="M93 53C93 47.7471 91.9654 42.5457 89.9552 37.6927C87.945 32.8396 84.9986 28.4301 81.2843 24.7157C77.5699 21.0014 73.1604 18.055 68.3073 16.0448C63.4543 14.0346 58.2529 13 53 13V53H93Z"
        />
        <path
          id="rightBottom"
          className={rightBottomAnimation}
          d="M53 93C58.2529 93 63.4543 91.9654 68.3073 89.9552C73.1604 87.945 77.5699 84.9986 81.2843 81.2843C84.9986 77.5699 87.945 73.1604 89.9552 68.3073C91.9654 63.4543 93 58.2529 93 53L53 53L53 93Z"
        />
        <path
          id="leftBottom"
          className={leftBottomAnimation}
          d="M13 53C13 58.2529 14.0346 63.4543 16.0448 68.3073C18.055 73.1604 21.0014 77.5699 24.7157 81.2843C28.4301 84.9986 32.8396 87.945 37.6927 89.9552C42.5457 91.9654 47.7471 93 53 93L53 53L13 53Z"
        />
        <path
          id="leftTop"
          className={leftTopAnimation}
          d="M53 13C47.7471 13 42.5457 14.0346 37.6927 16.0448C32.8396 18.055 28.4301 21.0014 24.7157 24.7157C21.0014 28.4301 18.055 32.8396 16.0448 37.6927C14.0346 42.5457 13 47.7471 13 53L53 53L53 13Z"
        />
        {isSelected && (
          <>
            <line className={verticalAnimation} x1="53" y1="13" x2="53" y2="93" strokeWidth="2" />
            <line className={horizontalAnimation} x1="93" y1="54" x2="13" y2="54" strokeWidth="2" />
          </>
        )}
      </g>
    </svg>
  );
};

export default SplitBillEvenly;
